import { useEffect, useCallback, useMemo } from 'react';
import { useChatStore } from './useChatStore';
import { ChatStreamService, type ConnectionState } from '../services/api/streaming/ChatStreamService';

/**
 * Hook pour gérer le streaming des réponses ADHA
 * Utilise Socket.IO pour la communication temps réel
 * Conforme à la documentation API v2.4.0
 * 
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const { 
 *     isStreaming, 
 *     streamingContent,
 *     useStreaming,
 *     setStreamingEnabled,
 *     connectionState,
 *     cancelStream
 *   } = useChatStreaming();
 *   
 *   return (
 *     <div>
 *       {isStreaming && (
 *         <>
 *           <p>{streamingContent}</p>
 *           <button onClick={cancelStream}>Arrêter</button>
 *         </>
 *       )}
 *       <button onClick={() => setStreamingEnabled(!useStreaming)}>
 *         {useStreaming ? 'Désactiver' : 'Activer'} streaming
 *       </button>
 *       <span>Status: {connectionState}</span>
 *     </div>
 *   );
 * }
 * ```
 */
export function useChatStreaming() {
  const {
    isStreaming,
    streamingContent,
    streamingMessageId,
    useStreaming,
    streamConnectionStatus,
    toggleStreaming,
    connectStreaming,
    disconnectStreaming,
    cancelCurrentStream
  } = useChatStore();

  /**
   * Annule le stream en cours
   */
  const cancelStream = useCallback(() => {
    if (isStreaming) {
      return cancelCurrentStream();
    }
    return false;
  }, [isStreaming, cancelCurrentStream]);

  /**
   * Active ou désactive le mode streaming
   */
  const setStreamingEnabled = useCallback((enabled: boolean) => {
    if (enabled !== useStreaming) {
      toggleStreaming();
    }
  }, [useStreaming, toggleStreaming]);

  /**
   * Vérifie si le streaming Socket.IO est supporté
   */
  const isStreamingSupported = useMemo(() => ChatStreamService.isSupported(), []);

  /**
   * État de la connexion Socket.IO
   */
  const connectionState: ConnectionState = streamConnectionStatus;

  /**
   * Connexion automatique au streaming si activé
   */
  useEffect(() => {
    if (useStreaming && isStreamingSupported && connectionState === 'disconnected') {
      connectStreaming().catch((error) => {
        console.warn('Connexion streaming échouée:', error);
      });
    }

    // Cleanup à la déconnexion du composant
    return () => {
      // Ne pas déconnecter automatiquement car d'autres composants peuvent utiliser le streaming
    };
  }, [useStreaming, isStreamingSupported, connectionState, connectStreaming]);

  /**
   * Reconnexion automatique en cas de perte de connexion
   */
  useEffect(() => {
    if (useStreaming && connectionState === 'error') {
      const reconnectTimer = setTimeout(() => {
        console.log('🔄 Tentative de reconnexion automatique...');
        connectStreaming().catch(console.warn);
      }, 5000);

      return () => clearTimeout(reconnectTimer);
    }
  }, [useStreaming, connectionState, connectStreaming]);

  return {
    // États
    isStreaming,
    streamingContent,
    streamingMessageId,
    useStreaming,
    isStreamingSupported,
    connectionState,
    
    // Actions
    setStreamingEnabled,
    toggleStreaming,
    connectStreaming,
    disconnectStreaming,
    cancelStream,

    // Helpers
    isConnected: connectionState === 'connected',
    isReconnecting: connectionState === 'reconnecting',
    hasError: connectionState === 'error',
  };
}

export default useChatStreaming;
